import { ChangeDetectionStrategy, Component, inject, Injector } from '@angular/core';
import { BehaviorSubject, forkJoin, of } from 'rxjs';
import { MpSelfEnrolmentFormService } from '../mp-self-enrolment-form-service';
import { MpSelfEnrolmentNavService } from '../mp-self-enrolment-nav-service';
import { BaseButtonSizeEnum } from '../../../../../shared/components/base-button/base-button-size.enum';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalEula } from '../../../../../../modals/modal-eula';
import { finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { MembersAPI } from '../../../../../../api/members-api';
import { ToastService } from '../../../../../../services/toast-service';
import { AttachmentApi } from '../../../../../../api/attachment-api';
import { AttachmentFile } from '../../../../../../models/users/dto/attachment-file';
import { CustomError } from '../../../../../../models/shared/custom-error';

@Component({
  selector: 'app-mp-self-enrolment-login-details',
  templateUrl: './mp-self-enrolment-login-details.component.html',
  styleUrls: ['./mp-self-enrolment-login-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MpSelfEnrolmentLoginDetailsComponent {
  private formService = inject(MpSelfEnrolmentFormService);
  private navService = inject(MpSelfEnrolmentNavService);
  private memberApi = inject(MembersAPI);
  private attachmentApi = inject(AttachmentApi);
  private toastService = inject(ToastService);
  private injector = inject(Injector);
  private ngbModal = inject(NgbModal);

  private _isLoading = new BehaviorSubject(false);
  public isLoading$ = this._isLoading.pipe(
    tap(isLoading => {
      if (isLoading) {
        this.formGroup.controls.acceptTerms.disable();
      } else {
        this.formGroup.controls.acceptTerms.enable();
      }
    })
  ) as BehaviorSubject<boolean>;

  public formGroup = this.formService.loginDetailsForm;
  public emailInUse$ = this.formService.emailInUse$;

  public formSubmitted = false;

  public customErrorMessages = {
    password: {
      passwordMinLength: $localize`Password must be at least 8 characters`,
      passwordMaxLength: $localize`Password cannot exceed 50 characters`
    },
    terms: {
      required: $localize`You must accept the terms and conditions to continue`
    }
  };

  public emailChanged(): void {
    this.emailInUse$.once(inUse => {
      if (inUse) {
        this.formService.setEmailInUse(false);
      }
    });
  }

  public openEulaModal(): void {
    ModalEula.open(this.ngbModal, this.injector);
  }

  private createEnrolmentSubmission() {
    return this.formService.constructRequestObject().pipe(
      take(1),
      switchMap(({ request, files }) => {
        return this.memberApi.mpSelfEnrollMember(request).pipe(
          map(res => ({
            files,
            res
          }))
        );
      }),
      switchMap(({ files, res }) => {
        if (!res?.length || !files?.length) {
          return of(null);
        }

        const attachmentUploads = files.map(uf => {
          const attachment = res.find(a => a.attachment.fileName === uf.file.name);
          if (!attachment) return of(null);

          const attachmentFile = new AttachmentFile(
            uf.file,
            attachment.id,
            uf.fileUrl,
            attachment.attachment.links[0].presignedUrl
          );
          return this.attachmentApi.uploadAttachmentFile(attachmentFile);
        });
        return forkJoin(attachmentUploads);
      })
    );
  }

  public goToLogin(): void {
    this.navService.goToLogin();
  }

  public submit(): void {
    this.formSubmitted = true;
    if (this.formGroup.valid) {
      this._isLoading.next(true);
      this.createEnrolmentSubmission()
        .pipe(
          take(1),
          finalize(() => this._isLoading.next(false))
        )
        .subscribe({
          next: () => {
            this.navService.goToEmailVerification(this.formGroup.controls.email.value);
          },
          error: (err: CustomError) => {
            if (err.code === 451) {
              this.formService.setEmailInUse(true);
            } else {
              this.toastService.publishError(err);
            }
          }
        });
    }
  }

  public goBack(): void {
    this.navService.goBack();
  }

  protected readonly BaseButtonSizeEnum = BaseButtonSizeEnum;
}
